import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compact } from 'lodash';
import Tooltip from 'reactstrap/lib/Tooltip'; // eslint-disable-line no-restricted-imports
import { venomHistory } from 'client/utils/history/venom-history';
import { TrackingHandler } from 'client/tracking/handler';
import { CompareVehicleTray } from 'site-modules/shared/components/compare-vehicle-tray/compare-vehicle-tray';
import { StickyTray } from 'site-modules/shared/components/sticky-tray/sticky-tray';
import { Storage } from 'site-modules/shared/utils/storage';
import { getPlural } from 'client/utils/plural';

export const MAX_CARS_TO_COMPARE = 4;

/**
 * HOC for compare vehicle tray functionality.
 * @param {function} WrappedComponent
 * @param {Object} options
 *  - @param {String} trackingParent - to apply tracking parent to CompareVehicleTray
 *  - @param {boolean} isVinCompare - to enable the navigation to inventory comparator (vin comparator) page.
 *  - @param {String} source - source of the page it's coming from
 *  - @param {boolean} sessionStoreUrl - to store the url(in session storage) of the page it's coming from so we can use it for the "Back to" link.
 *  - @param {Object} backFocusLink
 * @returns {CompareVehicleTrayWrapper}
 */
export function withCompareVehicleTray(WrappedComponent, options = {}) {
  const { trackingParent, source, isVinCompare = false, sessionStoreUrl = false, backFocusLink } = options;

  function CompareVehicleTrayWrapper(props) {
    const type = useRef(props.type === 'truck' ? 'trucks' : 'cars');
    const storage = useRef(new Storage('sessionStorage'));

    const [tooltipMessage, setTooltipMessage] = useState(null);
    const [tooltipTarget, setTooltipTarget] = useState(null);
    const [selectedCars, setSelectedCars] = useState([]);
    const [isTraySticky, setTraySticky] = useState(false);

    const onSelectCar = useCallback(
      (event, vehicleData) => {
        if (selectedCars.find(car => car.id === vehicleData.id)) {
          const updatedSelectedCars = selectedCars.filter(car => car.id !== vehicleData.id);

          setSelectedCars(updatedSelectedCars);
          setTraySticky(updatedSelectedCars.length > 0);
        } else if (selectedCars.length >= MAX_CARS_TO_COMPARE) {
          setTooltipMessage(`You can only compare ${MAX_CARS_TO_COMPARE} ${type.current} at a time.`);
          setTooltipTarget(event.target);

          TrackingHandler.fireEvent('compare_view_error', {
            event_data: {
              action_cause: 'checkbox_click',
              value: 'too_many_selected',
            },
          });
        } else {
          const updatedSelectedCars = selectedCars.concat(vehicleData);
          setSelectedCars(updatedSelectedCars);
          setTraySticky(updatedSelectedCars.length > 0);
        }
      },
      [selectedCars]
    );

    const clearSelectedCars = useCallback(() => {
      setTraySticky(false);
    }, []);

    const handleCompareClick = useCallback(
      event => {
        if (sessionStoreUrl) {
          storage.current.set('comparatorBackToUrl', window.location.pathname + window.location.search);
        }

        if (selectedCars.length === 0) {
          setTooltipMessage(`Select up to ${MAX_CARS_TO_COMPARE} ${type.current} to compare.`);
          setTooltipTarget(event.target);
          TrackingHandler.fireEvent('compare_view_error', {
            event_data: {
              action_cause: 'button_click',
              value: 'not_enough_selected',
            },
          });
        } else if (isVinCompare && source) {
          const vins = selectedCars.map(({ vin }) => vin).join(',');
          venomHistory.push(`/inventory-comparisons/?vins=${vins}&source=${source}`);
        } else {
          const vehicles = selectedCars
            .map(({ styleId, submodelSlug }, index) => `veh${index + 1}=${compact([styleId, submodelSlug]).join('|')}`)
            .join('&');
          venomHistory.push(`/car-comparisons/?${vehicles}`);
        }
      },
      [selectedCars]
    );

    useEffect(() => {
      if (!isTraySticky) {
        setSelectedCars([]);
      }
    }, [isTraySticky]);

    useEffect(() => {
      function hideTooltip() {
        if (!tooltipMessage) return;
        setTooltipMessage(null);
        setTooltipTarget(null);
      }

      document.addEventListener('click', hideTooltip, true);
      return () => {
        document.removeEventListener('click', hideTooltip, true);
      };
    }, [tooltipMessage]);

    const wrappedComponentProps = {
      ...props,
      selectedCars,
      onSelectCar,
    };

    return (
      <Fragment>
        <div className="vehicle-type-rankings-list">
          <WrappedComponent {...wrappedComponentProps} />
          <div aria-live="polite" aria-atomic="true" className="visually-hidden">
            Selected {selectedCars.length} {getPlural('vehicle', selectedCars.length)} for comparison
          </div>
          <StickyTray isSticky={isTraySticky}>
            <CompareVehicleTray
              clearSelectedCars={clearSelectedCars}
              handleCompareClick={handleCompareClick}
              selectedCars={selectedCars}
              onSelectCar={onSelectCar}
              trackingParent={trackingParent}
              backFocusLink={backFocusLink}
            />
          </StickyTray>
        </div>
        {!!tooltipMessage && (
          <Tooltip
            className="tooltip--white"
            innerClassName="p-1 heading-6-sub"
            isOpen
            placement="bottom"
            target={tooltipTarget}
            delay={{
              show: 100,
              hide: 200,
            }}
          >
            {tooltipMessage}
          </Tooltip>
        )}
      </Fragment>
    );
  }

  CompareVehicleTrayWrapper.propTypes = {
    type: PropTypes.string,
  };
  CompareVehicleTrayWrapper.defaultProps = {
    type: '',
  };
  CompareVehicleTrayWrapper.displayName = `WithCompareVehicleTray(${WrappedComponent.name})`;
  return CompareVehicleTrayWrapper;
}
